import { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import Input from '../../../../../components/Input/Input';
import { selectStyle, selectTheme } from '../styles';
import { ISoilDataVerticalDropForm } from './ISoilData';
import { setSoilData } from '../../../store/project/projectSlice';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { selectProject } from '../../../selector/conversion';
import { useTranslation } from 'react-i18next';
import * as BeMaccaferriStoreHelper from '../../../../api/store/BeMaccaferriStoreHelper';
import { IOption } from '../../../../../components/SelectCreate/SelectCreate';
import { IMaterialResponse, IPhysicalDataResponse } from '../../../../api/IApiResponse';
import { cleanResults } from '../../../store/project/resultsSlice';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { EConversionSIToImperial, conversionLabel, convertTo } from '../../../../conversion';
import { warning } from '../../../../../components/Alert/Alert';
import laneCoefficientImg from '../../../../images/lane_coefficient_image.png';
import './SoilData.scss';
import { UnitOfMeasure } from '../../../../enum/project';
let _soilMaterials: IMaterialResponse[];

const SoilDataVerticalDrop = () => {
    let timer = useRef<NodeJS.Timeout | null>(null);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const projectInfo = useAppSelector(selectProjectInfo);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    const project = useAppSelector(selectProject);
    const defaultValue: ISoilDataVerticalDropForm = { ...project.data?.soilData };
    const [form, setForm] = useState<ISoilDataVerticalDropForm>(defaultValue);
    const [soilTypeOptions, setSoilTypeOptions] = useState<IOption[]>();

    useEffect(() => {
        const fetchSoil = async () => {
            const responseSoilType = await BeMaccaferriStoreHelper.getIsAvailableSoilMaterials();
            if (!!responseSoilType.data) {
                _soilMaterials = responseSoilType.data;
                let soilTypeArray = responseSoilType.data.map(material => ({ value: material.id!, label: material.name! }));
                setSoilTypeOptions(soilTypeArray);
            }
        };
        fetchSoil();
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let name = e.target.name;
        let value = parseFloat(e.target.value);

        if (value < 0) {
            value = 0;
            warning(t('warning_not_be_lower', { value: 0 }));
        }

        setForm({
            ...form!,
            [name]: value,
        });
    };

    const handleInputPhysicalDataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let name = e.target.name;
        let value = parseFloat(e.target.value);

        if (value < 0) {
            value = 0;
            warning(t('warning_not_be_lower', { value: 0 }));
        }

        let physicalData: IPhysicalDataResponse = form.material!.physicalData![0];
        physicalData = {
            ...physicalData,
            [name]: value,
        };
        setForm({
            ...form,
            material: {
                ...form.material!,
                physicalData: [physicalData],
            },
        });
    };

    const handleChangeSoilType = (newValue: IOption) => {
        let material = _soilMaterials.find(mat => mat.id === newValue.value)!;
        if(isImperial){
            material= {
                ...material!,
                physicalData: material?.physicalData?.map(pd => ({
                    ...pd,
                    soilUnitWeight: parseFloat(convertTo(pd.soilUnitWeight ?? 0, EConversionSIToImperial.kNm3_to_lbfft3).toFixed(2)),
                }))
            }
        }
        setForm({
            ...form!,
            soilType: material?.name,
            material,
        });
    };

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            dispatch(cleanResults());
            dispatch(setSoilData(form as ISoilDataVerticalDropForm));
        }, 1000);
    }, [form]);

    return (
        <div className='d-flex flex-column gap-2'>
            <div className='d-flex justify-content-between align-items-center'>
                <label htmlFor='soilType'>{t('input_soilType')}</label>
                <Select
                    styles={selectStyle}
                    theme={selectTheme}
                    name='soilType'
                    options={soilTypeOptions}
                    value={{ label: form.material?.name, value: form.material?.id }}
                    onChange={(newValue: any) => handleChangeSoilType(newValue)}
                />
            </div>
            <Input
                type='number'
                title={t('input_frictionAngle')}
                name='soilFrictionAngle'
                value={form.material?.physicalData![0].soilFrictionAngle!}
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputPhysicalDataChange}
            />
            <Input
                type='number'
                title={t('input_unitWeight', { unit_measure: conversionLabel.kNm3_lbfft3[projectInfo.data?.unitOfMeasure as number] })}
                name='soilUnitWeight'
                value={form.material?.physicalData![0].soilUnitWeight}
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputPhysicalDataChange}
            />

            <Input
                type='number'
                title={t('input_granulometry', { unit_measure: conversionLabel.mm_in[projectInfo.data?.unitOfMeasure as number] })}
                name='granularMaterialDownstream'
                value={form.granularMaterialDownstream}
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />

            <Tabs>
                <TabList>
                    <Tab>{t('tab_gabionData')}</Tab>
                </TabList>
                <TabPanel className='d-flex flex-column gap-2'>
                    <Input
                        type='number'
                        title={t('input_porosity')}
                        name='gabionPorosity'
                        value={form.gabionPorosity}
                        className='d-flex justify-content-between align-items-center'
                        onChange={handleInputChange}
                    />
                    <Input
                        type='number'
                        title={t('input_frictionAngleSoilWeir')}
                        value={form.frictionAngleSoilWeir}
                        name='frictionAngleSoilWeir'
                        className='d-flex justify-content-between align-items-center'
                        onChange={handleInputChange}
                    />
                    <Input
                        type='number'
                        value={form.coefficientSoilFoundation}
                        title={t('input_coefficientSoilFoundation')}
                        name='coefficientSoilFoundation'
                        className='d-flex justify-content-between align-items-center'
                        onChange={handleInputChange}
                    />

                    <Input
                        type='number'
                        title={t('input_rockfillUnitWeight')}
                        value={form.rockFillUnitWeight}
                        name='rockFillUnitWeight'
                        className='d-flex justify-content-between align-items-center'
                        onChange={handleInputChange}
                    />
                </TabPanel>
            </Tabs>
        </div>
    );
};

export default SoilDataVerticalDrop;
